import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import React, { ReactElement, useCallback } from 'react'
import { useSelector } from 'react-redux'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import FilterSection from 'src/components/FilterSection/FilterSection'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { CreditCardStatus } from 'src/models/creditCard.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

/**
 * The three filters the budgets list offers. Plain arrays rather than the
 * legacy `Set`s: this is a prop crossing a component boundary, and a Set makes
 * every consumer reach for `setEquality` to answer "did this change".
 *
 * There is no `search` member, and that is not an omission. Legacy carried one
 * through its filters body and its URL, but the input that fed it has been
 * commented out in `BudgetsBoxWithFilters` for long enough that the value was
 * always `undefined` — a filter with no way to set it. Reproducing dead state
 * here would only make it look supported.
 */
export type BudgetsFilterValues = {
  statuses: CreditCardStatus[]
  users: string[]
  owners: string[]
}

/** An empty selection — the state the reset button restores. */
export const emptyBudgetsFilterValues: BudgetsFilterValues = {
  statuses: [],
  users: [],
  owners: [],
}

/** True when the user has narrowed the list at all. */
export const hasBudgetsFilters = (values: BudgetsFilterValues): boolean =>
  values.statuses.length > 0 ||
  values.users.length > 0 ||
  values.owners.length > 0

/** How many of the three filters are contributing, for the applied-count pip. */
export const budgetsFiltersAppliedCount = (
  values: BudgetsFilterValues,
): number =>
  (values.statuses.length > 0 ? 1 : 0) +
  (values.users.length > 0 ? 1 : 0) +
  (values.owners.length > 0 ? 1 : 0)

export type BudgetsFiltersProps = {
  className?: string
  /** The current selection. Controlled — this component holds no state. */
  values: BudgetsFilterValues
  /**
   * The whole next selection, every time any control changes. One callback
   * rather than three so the page can treat a filter change as a single
   * transition — it has to write all of them to the URL together anyway.
   */
  onChange: (next: BudgetsFilterValues) => void
  /** True while a filtered fetch is in flight; the flyout dims its controls. */
  loading?: boolean
  /** Users offered in BOTH dropdowns — budget user and account owner. */
  userOptions: UserOption[]
  /** Paging for the user dropdowns, which load their options ten at a time. */
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
}

const statusCheckboxRowCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
`

const filterLabelCss = css`
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )};
`

const dividerCss = css`
  margin: 20px 0px;
`

/**
 * The budgets list's filter bar — status, budget user, account owner (EX-40737).
 *
 * Lifted out of `BudgetsBoxWithFilters` so the redesigned list can keep the
 * filtering the legacy page had without inheriting the table, empty states and
 * `NavAwayBox` chrome bolted to it there.
 *
 * Renders the Filters BUTTON and its flyout, nothing else — it is mounted into
 * the table card's heading row. An earlier cut rendered a full header bar above
 * the card, which put two stacked cards, two headings and two type scales on
 * the page for one table.
 *
 * Routing-free by the `src/feature/**` boundary, which is the substantive
 * change from legacy rather than a mechanical move: `BudgetsBoxWithFilters`
 * read `selectQueryParams`, called `window.history.pushState` directly, and
 * debounced its own URL writes — three different owners of one piece of state.
 * Here the page owns the URL and hands the values down.
 */
export default function BudgetsFilters(
  props: BudgetsFiltersProps,
): ReactElement {
  const {
    className,
    values,
    onChange,
    loading = false,
    userOptions,
    userInfiniteScrollProps,
  } = props

  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)

  /**
   * Adds or removes one id from one of the three lists.
   *
   * Every control here is multi-select, so a click is always a toggle — the
   * dropdown reports the option that was clicked, not the resulting selection.
   */
  const toggle = useCallback(
    (key: keyof BudgetsFilterValues, value: string): void => {
      const current: string[] = values[key]
      onChange({
        ...values,
        [key]: current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value],
      })
    },
    [values, onChange],
  )

  const clear = useCallback(
    (key: keyof BudgetsFilterValues): void => {
      onChange({ ...values, [key]: [] })
    },
    [values, onChange],
  )

  const statusCheckbox = (
    status: CreditCardStatus,
    label: string,
    testId: string,
  ): ReactElement => (
    <label css={statusCheckboxRowCss}>
      <Checkbox
        checked={values.statuses.includes(status)}
        onChange={(): void => toggle('statuses', status)}
        aria-label={label}
        data-testid={testId}
      />
      {label}
    </label>
  )

  return (
    <FilterSection
      className={className}
      appliedFiltersCount={budgetsFiltersAppliedCount(values)}
      hideButtonChevron
      /*
        Chrome off, control only.

        `FilterSection` normally IS the header bar — a white, padded, bordered
        block with a title on the left. Here it contributes just its button and
        the flyout behind it, because this sits inside the table card's own
        heading row. `removePadding` is what drops both the 36px/40px block
        padding and the bottom rule that would otherwise cut across that row.

        Rendered through `FilterSection` rather than reproducing it: the choice
        between the two flyout implementations is itself flag-gated
        (`show_new_filters_flyout`), and a second copy of that decision would
        drift from the one every other filtered table makes.
      */
      removePadding
      filtersFlyoutProps={{
        title: translate('Budget Filters'),
        onReset: (): void => onChange(emptyBudgetsFilterValues),
        loading,
        filters: (
          <>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 12px;
              `}
            >
              <div css={filterLabelCss}>{translate('Budget Status')}</div>
              {statusCheckbox(
                CreditCardStatus.Active,
                translate('Active'),
                'activeCheckbox',
              )}
              {/*
                Inactive is widened to include Expired when the page builds its
                request — an expired budget reads as inactive to a user, and
                legacy did the same. The checkbox itself stays single-valued so
                the URL keeps one token per visible choice.
              */}
              {statusCheckbox(
                CreditCardStatus.Inactive,
                translate('Inactive'),
                'inactiveCheckbox',
              )}
            </div>
            <DividerLineDiv css={dividerCss} />
            <div css={filterLabelCss}>{translate('Budget User')}</div>
            <UserDropdown
              css={css`
                margin-top: 8px;
              `}
              isCreatable={false}
              placeholder={translateSingleString(locale, 'All budget users')}
              chooseOption={(value: string): void => toggle('users', value)}
              selectedValues={new Set(values.users)}
              optionGroups={[{ options: userOptions }]}
              isMulti
              onResetClick={(): void => clear('users')}
              infiniteScrollProps={userInfiniteScrollProps}
              minimalDisplay
              data-testid="budgetUserDropdown"
            />
            <DividerLineDiv css={dividerCss} />
            <div css={filterLabelCss}>{translate('Account Owner')}</div>
            <UserDropdown
              css={css`
                margin-top: 8px;
              `}
              isCreatable={false}
              placeholder={translateSingleString(locale, 'All account owners')}
              chooseOption={(value: string): void => toggle('owners', value)}
              selectedValues={new Set(values.owners)}
              optionGroups={[{ options: userOptions }]}
              isMulti
              onResetClick={(): void => clear('owners')}
              infiniteScrollProps={userInfiniteScrollProps}
              minimalDisplay
              data-testid="budgetCreatorDropdown"
            />
          </>
        ),
      }}
    />
  )
}
